import React, { FC, useCallback, useContext, useEffect, useRef } from 'react';
import { ReactSortable } from 'react-sortablejs';
import { UploaderFileListItem } from './_FileListItem/UploaderFileListItem';
import itemStyled from './_FileListItem/UploaderFileListItem.module.scss';
import { classnames } from '@src/utils/classnames';
import { UploaderContext } from '../../context';
import { BreakpointsContext } from '@src/providers/Breakpoints';
import { IUploaderFile } from '../../UploaderController';
import { MEDIA_UPLOADER_FILES_CONTAINER } from '../../consts';
import { Box } from '@src/atoms/Box';
import { TMediaUploaderPreviewMode } from '../../models';

import styles from './UploaderFileList.module.scss';

interface IUploaderFileListProps {
  files: IUploaderFile[];
  isScroll?: boolean;
  onZoom: (i: number) => void;
  previewMode: TMediaUploaderPreviewMode;
  showDeleteButton: boolean;
}

/**
 * @link https://github.com/SortableJS/react-sortablejs
 */
export const UploaderFileList: FC<IUploaderFileListProps> = ({
  isScroll,
  onZoom,
  files,
  previewMode,
  showDeleteButton,
}) => {
  const { controller } = useContext(UploaderContext);
  const { isMobile } = useContext(BreakpointsContext);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const prevFilesLength = useRef(files.length);

  const handleRemove = useCallback(
    (idx: number) => {
      const newFiles = [...files];
      newFiles.splice(idx, 1);
      controller.setStateFiles(newFiles);
    },
    [files, controller],
  );

  useEffect(() => {
    if (files.length > prevFilesLength.current && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
    prevFilesLength.current = files.length;
  }, [files]);

  const classes = classnames(isScroll && styles.scroll);
  const classeList = classnames(styles.listRoot, isMobile && styles.listRootMobile);
  return (
    <Box
      fullWidth
      data-elem={MEDIA_UPLOADER_FILES_CONTAINER}
      className={classes}
      ref={scrollRef}
      gap={{ DESKTOP: 16, MOBILE: 8 }}
      column
    >
      <ReactSortable
        list={files}
        setList={(v) => controller.setStateFiles(v)}
        className={classeList}
        handle={'.' + itemStyled.handle}
      >
        {files.map((val, idx) => (
          <UploaderFileListItem
            key={val.id}
            file={val}
            idx={idx}
            onRemove={handleRemove}
            onZoom={onZoom}
            previewMode={previewMode}
            showDeleteButton={showDeleteButton}
          />
        ))}
      </ReactSortable>
    </Box>
  );
};
